<template>
    <v-form v-model="isValid" @submit.prevent>
        <v-layout>
            <v-flex :class="searchFieldClass">
                <text-field-label
                    v-model="keyword"
                    :placeholder="placeholderName"
                    :disabled="textFieldDisabled"
                    :rules="[rules.maxChars1Arg(256)]"
                    clearable
                    @keydown.13="searchIfCanPress"
                />
            </v-flex>
            <v-flex>
                <v-btn
                    :loading="isLoading"
                    :disabled="buttonDisabled"
                    fab
                    small
                    color="accent"
                    @click="searchIfCanPress"
                >
                    <v-icon dark> search </v-icon>
                </v-btn>
            </v-flex>
        </v-layout>
    </v-form>
</template>

<script setup lang="ts">
import { readonly, toRefs, computed } from 'vue';
import { TextFieldLabel } from '../../atoms';
import { logicOr, logicNot } from '@vueuse/math';
import { useInputValidation } from '../../../composables/InputValidation';

/*
 * properties
 */
export interface Props {
    value?: string;
    placeholder?: string;
    loading?: boolean;
    disabled?: boolean;
    otherValid?: boolean;
    searchFieldClass?: string;
}
const props = withDefaults(defineProps<Props>(), {
    value: '',
    placeholder: undefined,
    loading: false,
    disabled: false,
    otherValid: false,
    searchFieldClass: 'search-field',
});
const { value, loading, disabled, placeholder, otherValid } = toRefs(props);

/*
 * emits
 */
interface Emits {
    (e: 'input', param: string): void;
    (e: 'click', param: string): void;
}
const emit = defineEmits<Emits>();

/*
 * composables
 */
const { isValid, rules } = useInputValidation();

/*
 * getter, setter
 */
const keyword = computed({
    get() {
        return value.value;
    },
    set(newValue) {
        emit('input', newValue);
    },
});
const isLoading = readonly(loading);
const isDisabled = readonly(disabled);
const placeholderName = readonly(placeholder);

const textFieldDisabled = logicOr(isDisabled, isLoading);
const buttonDisabled = logicOr(isDisabled, logicNot(isValid), isLoading, otherValid);

/**
 * methods
 */
function searchIfCanPress() {
    if (buttonDisabled.value) {
        return;
    }
    emit('click', keyword.value);
}
</script>

<style lang="scss" scoped>
.search-field {
    max-width: 35rem;
}
</style>
